import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import { Copy, Check, Play, X, ExternalLink, Loader2 } from "lucide-react";

const NUCLEUS_BRIDGE_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/0zo9n6c8_NucluesOS_Brand-11.webp";
const NUCLEUS_MAX_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/6mvq60gx_NucluesOS_Brand-13.webp";
const NUCLEUS_LENS_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/5sciu7sm_NucluesOS_Brand-12.webp";
const CATHAY_LOGO = "/assets/cathay_logo.png";
const AEROPLAY_ICON = "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/nmitj80k_Aeroplay-03-small.png";

// Product accent colors — used for card outlines matching each demo brand.
const BRIDGE_COLOR = "#A855F7"; // purple
const MAX_COLOR = "#EF4444"; // red/coral
const LENS_COLOR = "#22C55E"; // green

// Default (public) demos — deliberately generic, no airline-specific links.
const publicDemos = [
  {
    id: "bridge",
    kind: "logo",
    href: "https://bridge.nucleus-alpha.com/",
    logo: NUCLEUS_BRIDGE_LOGO,
    accent: BRIDGE_COLOR,
    embeddable: true,
    pill: { text: "Live", tone: "live" },
  },
  {
    id: "max",
    kind: "logo",
    href: "https://entertainment.nucleus-alpha.com",
    logo: NUCLEUS_MAX_LOGO,
    accent: MAX_COLOR,
    embeddable: true,
    pill: { text: "Live", tone: "live" },
  },
  {
    id: "lens",
    kind: "logo",
    href: "https://lens.nucleus-alpha.com/report/019d7177-b755-77e1-8ffd-0b4ad6234444",
    logo: NUCLEUS_LENS_LOGO,
    accent: LENS_COLOR,
    embeddable: true,
    pill: { text: "Live", tone: "live" },
  },
];

// Airlines flagged `useGenericDemos` resolve to the exact public demo set.
export const resolveDemos = (airline) => {
  const isPrivate = airline && airline.id !== "default";
  if (!isPrivate || airline.useGenericDemos) return publicDemos;
  return [...ctasToDemos(airline), publicDemos[0]];
};

const ctasToDemos = (airline) => {
  const extras = airline?.extraCTAs || [];
  if (!extras.length) return [];
  const map = { "nucleus-lens": NUCLEUS_LENS_LOGO };
  const accentMap = { "nucleus-lens": LENS_COLOR };
  return extras.map((c) => ({
    id: c.id,
    kind: "logo",
    href: c.href,
    logo: map[c.id] || (c.useAeroplayIcon ? AEROPLAY_ICON : c.useAirlineIcon ? airline.icon : null),
    accent: accentMap[c.id] || BRIDGE_COLOR,
    embeddable: c.id === "nucleus-lens",
    pill: c.id === "nucleus-lens" ? { text: "Live", tone: "live" } : null,
  }));
};

// Inline live preview: full-viewport iframe modal.
// If the target site sets X-Frame-Options or CSP frame-ancestors, some content
// may refuse to render inside the iframe — we surface a friendly fallback link.
export const DemoPreviewModal = ({ demo, isDark, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [embedBlocked, setEmbedBlocked] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Detect X-Frame-Options / CSP frame-ancestors blocks: if the iframe
    // never dispatches `load` within ~8s, treat it as blocked and surface
    // an explicit fallback state.
    const timeout = window.setTimeout(() => {
      setLoading((wasLoading) => {
        if (wasLoading) setEmbedBlocked(true);
        return false;
      });
    }, 8000);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(timeout);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const surface = isDark ? "#0b0b0d" : "#fbfbf9";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`${demo.id} live preview`}
      data-testid="demo-preview-backdrop"
    >
      <div
        className="absolute inset-0"
        style={{ backgroundColor: isDark ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}
        onClick={onClose}
      />

      <div
        className="relative w-full h-full max-w-[1400px] rounded-2xl overflow-hidden border shadow-2xl flex flex-col"
        style={{
          backgroundColor: surface,
          borderColor: `${demo.accent}66`,
          boxShadow: `0 40px 120px -30px ${demo.accent}55`,
        }}
      >
        {/* Chrome bar */}
        <div
          className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0"
          style={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }}
        >
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#FF5F57" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#FEBC2E" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#28C840" }} />
          </div>
          <div
            className="flex-1 h-7 rounded-md flex items-center px-3 text-[12px] font-mono truncate"
            style={{
              backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
              color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
            }}
          >
            {demo.href}
          </div>
          <a
            href={demo.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="demo-preview-launch"
            className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[11px] font-semibold"
            style={{ backgroundColor: `${demo.accent}22`, color: demo.accent }}
          >
            Open in new tab <ExternalLink className="w-3 h-3" />
          </a>
          <button
            onClick={onClose}
            data-testid="demo-preview-close"
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            style={{
              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
              color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)",
            }}
            aria-label="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Iframe (or blocked fallback) */}
        <div className="relative flex-1 min-h-0" style={{ backgroundColor: isDark ? "#000" : "#fff" }}>
          {loading && !embedBlocked && (
            <div className="absolute inset-0 flex items-center justify-center flex-col gap-3 z-10 pointer-events-none">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: demo.accent }} />
              <div
                className="text-[13px] font-medium"
                style={{ color: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.55)" }}
              >
                Loading live preview…
              </div>
            </div>
          )}
          {embedBlocked ? (
            <div
              data-testid="demo-preview-blocked"
              className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-4"
              style={{ color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)" }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${demo.accent}20`, color: demo.accent }}
              >
                <ExternalLink className="w-6 h-6" />
              </div>
              <div className="text-[18px] font-semibold tracking-tight">
                This preview can't be embedded.
              </div>
              <p
                className="text-[14px] max-w-[42ch] leading-relaxed"
                style={{ color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)" }}
              >
                The target site blocks framing for security. Open it in a new
                tab — your place in the pitch will be kept here.
              </p>
              <a
                href={demo.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="demo-preview-blocked-launch"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold"
                style={{ backgroundColor: demo.accent, color: "#0b0b0d" }}
              >
                Open live demo <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          ) : (
            <iframe
              src={demo.href}
              title={`${demo.id} preview`}
              className="w-full h-full border-0"
              allow="fullscreen"
              referrerPolicy="no-referrer-when-downgrade"
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              onLoad={() => setLoading(false)}
              data-testid="demo-preview-iframe"
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const Pill = ({ pill, isDark }) => {
  if (!pill) return null;
  const isLive = pill.tone === "live";
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0"
      style={{
        backgroundColor: isLive ? "rgba(16,185,129,0.15)" : (isDark ? "rgba(242,169,0,0.20)" : "rgba(242,169,0,0.15)"),
        color: isLive ? "#059669" : (isDark ? "#F2A900" : "#8B6600"),
      }}
    >
      {isLive && (
        <span className="relative flex w-1.5 h-1.5">
          <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
          <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-emerald-500" />
        </span>
      )}
      {pill.text}
    </span>
  );
};

const SlideExploreDemos = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const isPrivate = airline && airline.id !== "default";
  const [copiedId, setCopiedId] = useState(null);
  const [previewDemo, setPreviewDemo] = useState(null);
  const demos = resolveDemos(airline);

  const copyLink = async (id, href, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(href);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {}
  };

  const openPreview = (d, e) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewDemo(d);
  };

  return (
    <div>
      <div className="k-eyebrow" style={{ color: "var(--k-accent)" }}>
        Now, see it in action
      </div>
      <h2 className={`k-h2 mt-3 max-w-[16ch] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
        Explore the
        <br />
        <span style={{ color: "var(--k-accent)" }}>Aeroplay product.</span>
      </h2>
      <p className={`k-lead mt-5 max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
        {isPrivate && !airline.useGenericDemos
          ? `Live experiences configured for ${airline.name}. Best viewed on desktop.`
          : "Three live products, three angles on the same platform. Best viewed on desktop."}
      </p>

      <div className={`mt-10 grid gap-5`} style={{ gridTemplateColumns: `repeat(${demos.length}, minmax(0, 1fr))` }}>
        {demos.map((d) => (
          <a
            key={d.id}
            href={d.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`demo-card-${d.id}`}
            onClick={(e) => { if (d.embeddable) openPreview(d, e); }}
            className={`group relative rounded-2xl border-2 overflow-hidden transition-all hover:-translate-y-0.5 ${
              isDark ? "bg-white/[0.03] hover:bg-white/[0.06]" : "bg-white"
            }`}
            style={{
              borderColor: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)",
            }}
            onMouseEnter={(e) => {
              if (d.accent) {
                e.currentTarget.style.borderColor = d.accent;
                e.currentTarget.style.boxShadow = `0 12px 40px -20px ${d.accent}`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)";
              e.currentTarget.style.boxShadow = "";
            }}
          >
            {/* Big well — brand logo */}
            <div className={`flex flex-col items-center justify-center text-center px-4 py-8 ${isDark ? "bg-black/20" : "bg-black/[0.02]"}`}>
              <img
                src={d.logo}
                alt=""
                className="max-h-44 w-full object-contain"
              />
            </div>

            {/* Footer strip: pill + launch/copy */}
            <div className={`px-4 py-3 flex items-center justify-between gap-2 border-t ${isDark ? "border-white/10" : "border-black/10"}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Pill pill={d.pill} isDark={isDark} />
                {d.useCase && (
                  <span
                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: `${d.accent}20`,
                      color: d.accent,
                    }}
                  >
                    {d.useCase.label}
                    <img
                      src={d.useCase.logo}
                      alt={d.useCase.alt}
                      className="h-3 w-auto object-contain"
                      style={{ filter: isDark ? "brightness(0) invert(1)" : "none" }}
                    />
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={(e) => copyLink(d.id, d.href, e)}
                  data-testid={`demo-copy-${d.id}`}
                  title="Copy link"
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    isDark ? "text-white/60 hover:bg-white/[0.08]" : "text-black/55 hover:bg-black/[0.05]"
                  }`}
                >
                  {copiedId === d.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
                {d.embeddable && (
                  <button
                    onClick={(e) => openPreview(d, e)}
                    data-testid={`demo-preview-${d.id}`}
                    title="Preview inline"
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[12px] font-semibold"
                    style={{ backgroundColor: `${d.accent}20`, color: d.accent }}
                  >
                    <Play className="w-3 h-3" fill="currentColor" /> Preview
                  </button>
                )}
              </div>
            </div>
          </a>
        ))}
      </div>

      <p className={`mt-6 text-[13px] italic ${isDark ? "text-white/45" : "text-black/45"}`}>
        Click a card to preview it here, without leaving the pitch.
      </p>

      {previewDemo && (
        <DemoPreviewModal
          demo={previewDemo}
          isDark={isDark}
          onClose={() => setPreviewDemo(null)}
        />
      )}
    </div>
  );
};

export default SlideExploreDemos;
